import { forwardRef, useLayoutEffect, useMemo, useRef } from 'react'
import type React from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import {
  EffectComposer, N8AO, Bloom, SMAA, DepthOfField,
} from '@react-three/postprocessing'
import type { DepthOfFieldEffect } from 'postprocessing'
import * as THREE from 'three'
import { useAnimatie, useEffecten, useLicht } from '../scene/store'
import { useKiosk } from '../state/store'
import { FilmLookEffect } from './film/FilmLook'
import { OPNAME } from './film/opname'
import { LichtschachtenEffect, type Schacht } from './film/Lichtschachten'
import { focusPunt } from './CameraRig'
import { zonLicht } from './Licht'
import { useIndeling } from './indeling'
import { DAKLICHT_Y, DOCKS_X, DOCK_Z, ZON, daklichtRijen, dockOpen } from './daglicht'
import { halklok } from './omloop'

/**
 * De FilmLook als R3F-component. Eén instantie per composer; de uniforms worden
 * per render bijgewerkt in plaats van het effect opnieuw te bouwen, want een
 * nieuw effect betekent een nieuwe shadercompilatie — op de beurs-pc een
 * zichtbare hapering bij elke schuifbeweging in de Studio.
 */
const FilmLook = forwardRef<FilmLookEffect, {
  belichting: number
  aberratie: number
  lookContrast: number
  lookVerzadiging: number
  schaduwTint: string
  lichtTint: string
  tintSterkte: number
  merkrood: number
  vignet: number
  korrel: number
}>(function FilmLook(props, ref) {
  const effect = useMemo(() => new FilmLookEffect(), [])
  useLayoutEffect(() => {
    for (const [k, v] of Object.entries(props)) effect.zet(k as never, v)
  })
  // Opruimen doen we zelf. Géén `dispose={null}` op de primitive: R3F zet dat
  // letterlijk als eigenschap, `effect.dispose = null`, en dan faalt deze
  // opruiming — bij elke effectschakelaar in de Studio viel het canvas om.
  // Een primitive ruimt R3F sowieso nooit zelf op.
  useLayoutEffect(() => () => effect.dispose(), [effect])
  return <primitive ref={ref} object={effect} />
})

/** Eenheidskubus → wereld, met drie vrije assen: een scheve doos. */
function doos(o: THREE.Vector3, e1: THREE.Vector3, e2: THREE.Vector3, e3: THREE.Vector3) {
  return new THREE.Matrix4().set(
    e1.x, e2.x, e3.x, o.x,
    e1.y, e2.y, e3.y, o.y,
    e1.z, e2.z, e3.z, o.z,
    0, 0, 0, 1,
  ).invert()
}

/**
 * De lichtschachten als pass. De dozen komen uit dezelfde bron als het dak
 * (`daklichtRijen`): een schacht per rij daklichten, schuin naar beneden langs
 * de zonnerichting, en een per dockdeur, vlak de hal in.
 */
function Lichtschachten() {
  const camera = useThree((s) => s.camera)
  const licht = useLicht()
  const anim = useAnimatie()
  const ind = useIndeling()
  const effect = useMemo(() => new LichtschachtenEffect(camera), [camera])
  useLayoutEffect(() => () => effect.dispose(), [effect])

  const docksVanaf = useRef(0)

  useLayoutEffect(() => {
    const schachten: Schacht[] = []
    if (licht.daklichten && licht.lichtschachten) {
      const neer = ZON.clone().multiplyScalar(-DAKLICHT_Y / ZON.y)
      const kleur = new THREE.Color(licht.zonKleur).multiplyScalar(licht.zonSterkte * 0.0045)
      for (const rij of daklichtRijen(ind)) {
        const b = rij.vakken[0].breedte
        const l = rij.x1 - rij.x0
        schachten.push({
          inverse: doos(
            new THREE.Vector3(rij.x0, DAKLICHT_Y, rij.z - b / 2),
            new THREE.Vector3(l, 0, 0),
            new THREE.Vector3(0, 0, b),
            neer,
          ),
          kleur,
          zon: true,
          maat: new THREE.Vector3(l, b, neer.length()),
        })
      }
    }
    docksVanaf.current = schachten.length
    if (licht.daklichten) {
      for (const x of DOCKS_X) {
        schachten.push({
          inverse: doos(
            new THREE.Vector3(x - 1.6, 0.02, DOCK_Z + 0.12),
            new THREE.Vector3(0, 4.1, 0),
            new THREE.Vector3(3.2, 0, 0),
            new THREE.Vector3(0, -0.9, 11),
          ),
          kleur: new THREE.Color(),
          zon: false,
          maat: new THREE.Vector3(4.1, 3.2, 11),
        })
      }
    }
    effect.zetSchachten(schachten)
  }, [effect, ind, licht.daklichten, licht.lichtschachten, licht.zonKleur, licht.zonSterkte])

  const dockKleur = useMemo(() => new THREE.Color(), [])
  const dockBasis = useMemo(() => new THREE.Color('#aac0e0'), [])

  useFrame(() => {
    effect.zonLicht = zonLicht.current
    effect.zetSterkte(licht.schachtSterkte)
    if (!licht.daklichten) return
    // Het daglicht door de dockdeuren gaat mee met de deur (`Props.tsx`).
    const tijd = anim.dockdeuren ? halklok.t : 0
    DOCKS_X.forEach((_, i) => {
      const open = dockOpen(tijd, i)
      dockKleur.copy(dockBasis).multiplyScalar(0.045 * licht.daglicht * Math.pow(open, 1.5))
      effect.zetKleur(docksVanaf.current + i, dockKleur)
    })
  })

  return <primitive object={effect} />
}

/**
 * Scherptediepte alleen als er een onderwerp is: een geopende hotspot, met
 * de machine op 8 tot 17 m. Op de overzichtsschermen kijkt de camera naar een
 * punt diep in de hal, en focus dáárop maakt alles ervoor onscherp — op de
 * eerste proefafdruk was het hoofdscherm één waas. Een overzicht is in film
 * ook altijd scherp; de ondiepe focus hoort bij de close-up.
 *
 * Een eigen component, en dat is geen smaak. Luistert `Effecten` zelf naar het
 * scherm, dan hertekent hij bij elke tik, krijgt de composer nieuwe kinderen,
 * en bouwt hij al zijn passes opnieuw op — met een nieuwe shadercompilatie
 * voor elke pass. Dat was een hapering bij elke navigatie, en onder de
 * softwarerenderer van het afdrukscript een transitieslot dat na 30 s nog dicht
 * stond.
 */
function DofSturing({ dof, bokehDoel, bereik }: {
  dof: React.RefObject<DepthOfFieldEffect>
  bokehDoel: number
  bereik: number
}) {
  const camera = useThree((s) => s.camera)
  const hotspot = useKiosk((s) => s.hotspot)
  const scherm = useKiosk((s) => s.scherm)
  const onderwerp = scherm === 'automatisering' && hotspot !== null
  const bokeh = useRef(0)

  useFrame((_, delta) => {
    const d = dof.current
    if (!d) return
    if (d.target !== focusPunt) d.target = focusPunt
    const afstand = camera.position.distanceTo(focusPunt)
    d.cocMaterial.focusRange = Math.max(2, afstand * bereik)
    // Traag in- en uitfaden, gelijk op met de cameravlucht: een focus die
    // tegelijk met de eerste tik verspringt, leest als een fout.
    const doel = onderwerp ? bokehDoel : 0
    bokeh.current += (doel - bokeh.current) * (1 - Math.pow(0.02, Math.min(delta, 0.1)))
    d.bokehScale = bokeh.current
  })

  return null
}

/**
 * Post-processing. Dit is de grootste enkele sprong in beeldkwaliteit: zonder
 * ambient occlusion heeft niets contactschaduw en zweeft elk object optisch los
 * van de vloer en van zijn buren — dat is wat de scène als "blokken" laat lezen.
 *
 * N8AO komt mee met @react-three/postprocessing v2 en is aanzienlijk beter dan
 * de ingebouwde SSAO. De v2-lijn is bovendien verplicht: v3 vereist React 19.
 *
 * ## De volgorde is de camera
 *
 * AO → scherptediepte → bloom → filmlook → SMAA. Dat is de volgorde waarin het
 * in een echte camera gebeurt: licht in de ruimte (AO), dan de lens (focus,
 * overstraling), dan de film (curve, korrel). Bloom vóór de tone mapping is
 * geen detail: in de HDR-buffer weet bloom nog hoe fel een armatuur is, na de
 * curve is alles boven 1 al samengeperst en gloeit een witte doos net zo hard
 * als een lamp.
 */
export function Effecten() {
  const e = useEffecten()
  const licht = useLicht()
  const dof = useRef<DepthOfFieldEffect>(null)
  // De schachtenpass draaide ook als hij niets te tekenen had: een lege lijst
  // dozen, maar wel een beeldvullende raymarch per frame. Nu zit hij alleen in
  // de keten als er schachten zijn (het lichte profiel zet ze uit).
  const schachten = licht.daklichten && licht.lichtschachten

  // Sleutel forceert een nieuwe composer als effecten aan/uit gaan; anders blijft
  // een uitgeschakelde pass in de keten hangen.
  const sleutel = `${e.ao}-${e.bloom}-${e.dof}-${e.smaa}-${schachten}`

  return (
    <>
      <DofSturing dof={dof} bokehDoel={e.dofBokeh} bereik={e.dofBereik} />
      <EffectComposer key={sleutel} multisampling={0} enableNormalPass frameBufferType={THREE.HalfFloatType}>
        {/* In de film (`OPNAME`) op volle resolutie en met de meeste monsters:
            realtime is dat te duur, offline niet. */}
        {e.ao ? (
          <N8AO
            aoRadius={e.aoStraal}
            intensity={e.aoSterkte}
            distanceFalloff={1}
            screenSpaceRadius={false}
            halfRes={!OPNAME.aan}
            quality={OPNAME.aan ? OPNAME.ao : undefined}
            color="#05070c"
          />
        ) : <></>}

        {schachten ? <Lichtschachten /> : <></>}

        {e.dof ? (
          <DepthOfField ref={dof} bokehScale={0} resolutionScale={OPNAME.aan ? 1 : 0.5} focusDistance={20} focusRange={12} />
        ) : <></>}

        {e.bloom ? (
          <Bloom
            intensity={e.bloomSterkte}
            luminanceThreshold={e.bloomDrempel}
            luminanceSmoothing={0.35}
            mipmapBlur
            radius={0.6}
            levels={8}
          />
        ) : <></>}

        <FilmLook
          belichting={e.belichting}
          aberratie={e.aberratie}
          lookContrast={1 + e.contrast}
          lookVerzadiging={1 + e.verzadiging}
          schaduwTint={e.schaduwTint}
          lichtTint={e.lichtTint}
          tintSterkte={e.gradatie}
          merkrood={e.merkrood}
          vignet={e.vignette ? e.vignetteSterkte : 0}
          korrel={e.korrel}
        />

        {e.smaa ? <SMAA /> : <></>}
      </EffectComposer>
    </>
  )
}
